
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type EntityType = 'candidate' | 'job' | 'client';

interface DeletionResult {
  success: boolean;
  error?: string;
  entity_name?: string;
  related_entities_count?: number;
}

export const useSoftDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      entityType, 
      entityId, 
      reason 
    }: { 
      entityType: EntityType; 
      entityId: string; 
      reason?: string;
    }): Promise<DeletionResult> => {
      const functionName = `soft_delete_${entityType}` as const;
      
      const { data, error } = await supabase.rpc(functionName as any, {
        [`p_${entityType}_id`]: entityId,
        p_reason: reason || null
      });

      if (error) {
        throw new Error(error.message);
      }

      // Ensure we return a properly typed DeletionResult
      return {
        success: data?.success || false,
        error: data?.error,
        entity_name: data?.candidate_name || data?.job_title || data?.client_name,
        related_entities_count: data?.related_entities_count || 0
      } as DeletionResult;
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: ['candidates'] });
        queryClient.invalidateQueries({ queryKey: ['jobs'] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        
        toast.success(
          `${variables.entityType.charAt(0).toUpperCase() + variables.entityType.slice(1)} deleted successfully`,
          {
            description: data.related_entities_count 
              ? `${data.related_entities_count} related records were also removed`
              : undefined
          }
        );
      } else {
        toast.error(data.error || 'Failed to delete record');
      }
    },
    onError: (error: Error) => {
      toast.error('Deletion failed', {
        description: error.message
      });
    }
  });
};
